import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import posthog from 'posthog-js';
import { Link } from 'react-router-dom';
import { Brain, TrendUp, Lightning, ArrowRight, MagnifyingGlass, CurrencyDollar, ChartBar, Warning, CheckCircle, Eye, Clock, ShoppingBag, UsersThree } from '@phosphor-icons/react';
import { Container, Title, Text, Button, Paper, Group, Stack, Box, SimpleGrid, ThemeIcon, Slider, Modal } from '@mantine/core';

import PlatformRotator from '../components/PlatformRotator';
import ClientLogos from '../components/ClientLogos';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';

// The agent's reasoning pipeline: every scenario flows through the same four
// stages, so the homepage shows HOW Droid works (detect -> diagnose -> act ->
// result), not just a stream of log lines. Each scenario maps to a real
// capability (health check, bid/budget control, negative keywords).
// Display labels (tags) + scenarios come from i18n (landing.flow.*).
const STAGES = [
  { id: 'signal', icon: Warning, accent: '#f59e0b', chip: 'rgba(245,158,11,0.14)' },
  { id: 'diagnosis', icon: Brain, accent: '#60a5fa', chip: 'rgba(29,78,216,0.18)', thinking: true },
  { id: 'action', icon: Lightning, accent: '#1D4ED8', chip: 'rgba(29,78,216,0.18)' },
  { id: 'result', icon: CheckCircle, accent: '#10b981', chip: 'rgba(16,185,129,0.14)' },
];

// Icons/accents for the "what else does Droid catch?" modal. Titles + items come
// from i18n (landing.modal.cases), merged by index.
const USE_CASE_ICONS = [
  { icon: Eye, accent: '#f59e0b' },
  { icon: CurrencyDollar, accent: '#1D4ED8' },
  { icon: MagnifyingGlass, accent: '#1D4ED8' },
  { icon: Clock, accent: '#1D4ED8' },
  { icon: ShoppingBag, accent: '#10b981' },
  { icon: UsersThree, accent: '#1D4ED8' },
];


function RoiCalculator() {
  const { t } = useTranslation();
  const [budget, setBudget] = useState(5000);
  const [dragging, setDragging] = useState(false);
  const [badass, setBadass] = useState(false);
  const [tracked, setTracked] = useState(false);

  const agencyCost = Math.max(1000, Math.round(budget * 0.15));
  const droidCost = 299;
  const savings = agencyCost - droidCost;
  const yearSavings = savings * 12;
  const estClicks = Math.round(budget / 3.5);
  const estClicksDroid = Math.round((budget + savings) / 3.5);
  const extraClicks = estClicksDroid - estClicks;
  const fmt = (v) => v.toLocaleString('pl-PL');

  return (
    <div className="mt-6 max-w-md mx-auto relative">
      <div className="px-1">
        <Group justify="space-between" mb={10}>
          <Text size="sm" c="gray.5">{t('landing.roi.budgetLabel')}</Text>
          <Text size="lg" fw={700} c="white">{t('landing.roi.budgetPerMonth', { value: fmt(budget) })}</Text>
        </Group>
        <Slider
          value={budget}
          onChange={(v) => {
            setBudget(v);
            setDragging(true);
            if (!tracked) { posthog.capture('roi_calculator_used'); setTracked(true); }
            if (v >= 50000 && !badass) {
              setBadass(true);
              posthog.capture('roi_calculator_max_badass');
              setTimeout(() => setBadass(false), 4000);
            }
          }}
          onChangeEnd={() => setDragging(false)}
          min={1000}
          max={50000}
          step={500}
          color="brand"
          size="xl"
          label={null}
          mb="lg"
          thumbSize={30}
          className="roi-slider"
          styles={{
            thumb: {
              borderWidth: 4,
              borderColor: '#ffffff',
              backgroundColor: '#1D4ED8',
              boxShadow: '0 4px 14px rgba(80,70,229,0.5)',
              transition: 'transform 0.15s, border-color 0.15s',
            },
            track: { backgroundColor: 'rgba(255,255,255,0.16)' },
            bar: { backgroundColor: '#1D4ED8' },
          }}
        />
        <Group justify="space-between">
          <Text size="sm" c="gray.5">{t('landing.roi.agencyLabel')} <Text span td="line-through" style={{ opacity: 0.5 }}>{t('landing.roi.amount', { value: fmt(agencyCost) })}</Text></Text>
          <Text size="sm" c="gray.5">{t('landing.roi.droidLabel')} <Text span fw={700} c="white">{t('landing.roi.droidPrice')}</Text></Text>
          <Text size="sm" fw={700} c="white">{t('landing.roi.youSave', { value: fmt(savings) })}</Text>
        </Group>
      </div>

      {/* Badass easter egg */}
      <div className={`roi-badass ${badass ? 'roi-badass-visible' : ''}`}>
        <div style={{ background: 'white', borderRadius: 20, padding: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.12)', position: 'relative' }}>
          <img src="/badass.png" alt="" style={{ width: 180, borderRadius: 14, display: 'block' }} />
          <div style={{
            position: 'absolute', bottom: -10, left: '50%', marginLeft: -10,
            width: 0, height: 0,
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderTop: '10px solid white',
          }} />
        </div>
      </div>

      {/* Detail popup on drag */}
      <div className={`roi-popup ${dragging && !badass ? 'roi-popup-visible' : ''}`}>
        <Paper radius="lg" px="lg" py="md" shadow="lg" withBorder style={{ background: 'white' }}>
          <Text size="xs" fw={600} c="dark" mb="xs">{t('landing.roi.withDroidInstead')}</Text>
          <Stack gap={6}>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{t('landing.roi.yearlySavings')}</Text>
              <Text size="xs" fw={700} c="dark">{t('landing.roi.amount', { value: fmt(yearSavings) })}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{t('landing.roi.extraClicks')}</Text>
              <Text size="xs" fw={700} c="dark">+{fmt(extraClicks)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{t('landing.roi.monitoring')}</Text>
              <Text size="xs" fw={700} c="dark">24/7</Text>
            </Group>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{t('landing.roi.reactionTime')}</Text>
              <Text size="xs" fw={700} c="dark">&lt; 30 min</Text>
            </Group>
          </Stack>
        </Paper>
      </div>
    </div>
  );
}

function ThinkingDots() {
  return (
    <span className="inline-flex items-center gap-[3px] ml-1">
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="w-1 h-1 rounded-full bg-white/80 animate-thinking-dot"
          style={{ animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </span>
  );
}

function FlowNode({ stage, tag, text, revealed, active }) {
  const Icon = stage.icon;
  return (
    <div className="flex-1 min-w-0 w-full">
      <div
        className="flex h-full flex-col items-center text-center rounded-2xl px-5 py-7 transition-all duration-500"
        style={{
          background: 'linear-gradient(160deg,#1D4ED8 0%,#2563EB 55%,#3B82F6 100%)',
          opacity: revealed ? 1 : 0.55,
          boxShadow: active
            ? '0 14px 34px rgba(29,78,216,0.38)'
            : '0 6px 18px rgba(29,78,216,0.18)',
          transform: active ? 'translateY(-2px)' : 'none',
        }}
      >
        {/* bigger white icon on a soft translucent tile */}
        <span
          className={`flex items-center justify-center w-16 h-16 rounded-2xl shrink-0 mb-4 transition-all duration-500 ${revealed ? 'af-badge-in' : ''}`}
          style={{ background: 'rgba(255,255,255,0.16)' }}
        >
          <Icon size={32} weight="bold" style={{ color: '#fff' }} />
        </span>

        <div className="flex items-center justify-center gap-1.5 mb-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-white/70">
            {tag}
          </span>
          {stage.thinking && active && <ThinkingDots />}
        </div>

        <div className="min-h-[48px] w-full">
          {revealed ? (
            <p key={text} className="af-text-in text-[15px] font-medium leading-snug text-white">{text}</p>
          ) : (
            <div className="space-y-2 pt-1 flex flex-col items-center w-full">
              <div className="h-2 rounded af-shimmer-light" style={{ width: '80%' }} />
              <div className="h-2 rounded af-shimmer-light" style={{ width: '55%' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Connector({ on }) {
  const grad = 'linear-gradient(90deg,#1D4ED8,#3B82F6)';
  const gradV = 'linear-gradient(180deg,#1D4ED8,#3B82F6)';
  return (
    <div className="flex items-center justify-center shrink-0 md:px-1 py-1 md:py-0">
      {/* horizontal track (desktop), aligned with the icon row */}
      <div className="hidden md:block relative w-10 h-[2px] rounded" style={{ background: 'rgba(29,78,216,0.16)' }}>
        <div className={`af-fill-x absolute inset-0 rounded ${on ? 'on' : ''}`} style={{ background: grad }} />
      </div>
      {/* vertical track (mobile) */}
      <div className="md:hidden relative h-6 w-[2px] rounded" style={{ background: 'rgba(29,78,216,0.16)' }}>
        <div className={`af-fill-y absolute inset-0 rounded ${on ? 'on' : ''}`} style={{ background: gradV }} />
      </div>
    </div>
  );
}

function UseCasesModal({ opened, onClose }) {
  const { t } = useTranslation();
  const cases = t('landing.modal.cases', { returnObjects: true });
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="min(64rem, 94vw)"
      radius="md"
      centered
      overlayProps={{ backgroundOpacity: 0.5, blur: 3 }}
      title={<span className="text-base font-semibold text-gray-900">{t('landing.modal.title')}</span>}
    >
      <p className="text-sm text-gray-600 leading-relaxed mb-5">
        {t('landing.modal.intro')}
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {USE_CASE_ICONS.map((uc, i) => {
          const Icon = uc.icon;
          const c = (Array.isArray(cases) && cases[i]) || { title: '', items: [] };
          return (
            <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-2.5 mb-2.5">
                <span
                  className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                  style={{ background: `${uc.accent}1a` }}
                >
                  <Icon size={17} weight="bold" style={{ color: uc.accent }} />
                </span>
                <h3 className="text-[13px] font-semibold text-gray-900 leading-tight">{c.title}</h3>
              </div>
              <ul className="space-y-1.5">
                {(c.items || []).map((it) => (
                  <li key={it} className="flex gap-2 text-[12.5px] text-gray-600 leading-snug">
                    <CheckCircle size={13} weight="bold" className="text-indigo-500 mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-between gap-4 rounded-xl border border-indigo-100 bg-indigo-50 p-4">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-gray-900">{t('landing.modal.worksTitle')}</p>
          <p className="text-xs text-gray-600">{t('landing.modal.worksDesc')}</p>
        </div>
        <Button component={Link} to="/rejestracja" radius="md" className="shrink-0" rightSection={<ArrowRight size={16} weight="bold" />}>
          {t('landing.modal.activateCta')}
        </Button>
      </div>
    </Modal>
  );
}

function AgentFlow() {
  const { t } = useTranslation();
  const scenarios = t('landing.flow.scenarios', { returnObjects: true });
  const stageTags = t('landing.flow.stages', { returnObjects: true });
  const [scenario, setScenario] = useState(0);
  // step = how many stages are revealed (1..4). Starts at 1 so the signal is
  // visible immediately; resets to a new scenario after the result lands.
  const [step, setStep] = useState(1);
  const [useCasesOpen, setUseCasesOpen] = useState(false);

  const openUseCases = () => {
    try { posthog.capture('agent_usecases_opened'); } catch (e) { /* noop */ }
    setUseCasesOpen(true);
  };

  const scenarioCount = Array.isArray(scenarios) ? scenarios.length : 1;

  useEffect(() => {
    let tmr;
    if (step < 4) {
      tmr = setTimeout(() => setStep(s => s + 1), 1250);
    } else {
      tmr = setTimeout(() => {
        setScenario(s => (s + 1) % scenarioCount);
        setStep(1);
      }, 2600);
    }
    return () => clearTimeout(tmr);
  }, [step, scenario, scenarioCount]);

  const sc = (Array.isArray(scenarios) && scenarios[scenario]) || { signal: '', diagnosis: '', action: '', result: '' };
  const texts = [sc.signal, sc.diagnosis, sc.action, sc.result];

  return (
    <div className="relative mt-16 max-w-5xl mx-auto">
      <div className="mb-7 flex justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#1D4ED8]/20 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#1D4ED8]">
          <Lightning size={13} weight="fill" />
          {t('landing.flow.useCasesBadge')}
        </span>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-stretch md:gap-0">
        {STAGES.map((stage, i) => (
          <React.Fragment key={stage.id}>
            <FlowNode
              stage={stage}
              tag={Array.isArray(stageTags) ? stageTags[i] : ''}
              text={texts[i]}
              revealed={step > i}
              active={step === i + 1}
            />
            {i < STAGES.length - 1 && <Connector on={step > i + 1} />}
          </React.Fragment>
        ))}
      </div>

      {/* CTA to the full capabilities modal */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={openUseCases}
          className="group inline-flex items-center gap-2 rounded-full border border-[#1D4ED8]/25 bg-white px-4 py-2 text-[13px] font-medium text-[#1D4ED8] shadow-sm transition hover:bg-[#1D4ED8] hover:text-white hover:shadow-md"
        >
          <MagnifyingGlass size={15} weight="bold" />
          {t('landing.flow.otherProblems')}
          <ArrowRight size={14} weight="bold" className="opacity-70 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
      <UseCasesModal opened={useCasesOpen} onClose={() => setUseCasesOpen(false)} />
    </div>
  );
}

export default function LandingPage() {
  const { t } = useTranslation();
  const featureItems = t('landing.features.items', { returnObjects: true });
  return (
    <div className="min-h-screen bg-[#F6F5F4]">
      <SEO
        description={t('landing.seo.description')}
        path="/"
      />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#F6F5F4] overflow-hidden">

        <Container size="xl" className="relative" pt={40} pb={56}>
          {/* Mailjet-style rounded dark hero card wrapping the headline + CTA */}
          <div className="relative overflow-hidden rounded-[2rem] md:rounded-[2.5rem] bg-[#0b0b0d] px-5 sm:px-10 pt-14 md:pt-24 pb-8 md:pb-12 shadow-2xl ring-1 ring-white/5">
            {/* soft brand glow at the top */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-1/2"
              style={{ background: 'radial-gradient(700px circle at 50% -10%, rgba(80,70,229,0.35), transparent 65%)' }}
            />
            <div className="relative text-center max-w-4xl mx-auto">
              <Title
                order={1}
                c="white"
                fz={{ base: 36, md: 56 }}
                lh={1.1}
                mb="md"
              >
                {t('landing.hero.titleLine1')}
                <br />
                <PlatformRotator prefix="" />
                <br />
                {t('landing.hero.titleLine3')}
              </Title>

              <Text
                size="xl"
                c="gray.4"
                maw={640}
                mx="auto"
                mb="xl"
                lh={1.7}
              >
                {t('landing.hero.subtitle1')}<br />{t('landing.hero.subtitle2')}
              </Text>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  component={Link}
                  to="/rejestracja"
                  size="xl"
                  radius="md"
                  rightSection={<ArrowRight size={20} weight="bold" />}
                >
                  {t('landing.hero.cta')}
                </Button>
                <div className="text-center sm:text-left">
                  <Text c="gray.5" size="sm">
                    <Text span fw={700} c="white" fz={24}>{t('landing.hero.priceFrom')}</Text> {t('landing.hero.perMonth')}
                  </Text>
                  <Text c="gray.5" fw={600} size="sm" mt={4}>{t('landing.hero.trial')}</Text>
                </div>
              </div>

              {/* No campaigns yet? callout */}
              <Text size="sm" c="gray.5" mt="lg">
                {t('landing.hero.noCampaignQuestion')} <Text span c="white" fw={500}>{t('landing.hero.noCampaignAnswer')}</Text>
                <br />
                {t('landing.hero.noCampaignDesc')}
              </Text>
            </div>

            {/* ROI Calculator - inside the dark hero card */}
            <div className="relative">
              <RoiCalculator />
            </div>
          </div>

          {/* Client logos */}
          <ClientLogos />

          {/* Agent reasoning pipeline - animated */}
          <AgentFlow />
        </Container>
      </section>

      {/* Features teaser - 3 items */}
      <section className="bg-[#F6F5F4] pt-10 md:pt-12 pb-24">
        <Container size="xl">
          <Title
            order={2}
            ta="center"
            mb="xs"
           
            fz={{ base: 30, md: 36 }}
          >
            {t('landing.features.heading')}
          </Title>
          <Text c="dimmed" ta="center" mb={48} maw={640} mx="auto">
            {t('landing.features.subheading')}
          </Text>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {[Brain, Lightning, TrendUp].map((Icon, i) => (
              <Paper key={i} radius="lg" p="lg" withBorder className="hover:shadow-md hover:border-indigo-200 transition-all">
                <ThemeIcon size={56} radius="md" variant="light" color="brand" mb="sm">
                  <Icon size={32} weight="duotone" />
                </ThemeIcon>
                <Title order={4} mb="xs">{featureItems[i]?.title}</Title>
                <Text c="dimmed" lh={1.7}>{featureItems[i]?.desc}</Text>
              </Paper>
            ))}
          </SimpleGrid>

          <Group justify="center" mt="xl">
            <Button
              component={Link}
              to="/funkcje"
              variant="subtle"
              color="brand"
              rightSection={<ArrowRight size={16} weight="bold" />}
            >
              {t('landing.features.allFeaturesCta')}
            </Button>
          </Group>
        </Container>
      </section>

      {/* CTA */}
      <section className="bg-[#1D4ED8] py-20">
        <Container size="md" ta="center">
          <Title
            order={2}
            c="white"
            mb="sm"
           
            fz={{ base: 30, md: 36 }}
          >
            {t('landing.ctaSection.heading')}
          </Title>
          <Text c="indigo.1" size="lg" mb="xl" maw={640} mx="auto">
            {t('landing.ctaSection.subheading')}
          </Text>
          <Group justify="center" gap="md">
            <Button
              component={Link}
              to="/rejestracja"
              size="xl"
              radius="md"
              rightSection={<ArrowRight size={20} weight="bold" />}
              className="cta-white-btn"
            >
              {t('landing.ctaSection.tryFree')}
            </Button>
            <Button
              component={Link}
              to="/cennik"
              size="xl"
              variant="outline"
              radius="md"
              className="cta-outline-btn"
            >
              {t('landing.ctaSection.seePricing')}
            </Button>
          </Group>
        </Container>
      </section>

      <Footer />
    </div>
  );
}
